import { QueueListIcon, ServerStackIcon } from '@heroicons/react/24/outline';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyApplication,
  TechnologyApplicationUpsertPayload,
  TechnologySecret,
  createTechnologyApplication,
  deleteTechnologyApplication,
  getTechnologyApplication,
  listTechnologyApplications,
  listTechnologySecrets,
  updateTechnologyApplication,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_APPLICATION_STATUS_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';
import { TechnologyApplicationInventoryModal } from './TechnologyApplicationInventoryModal';
import { TechnologyApplicationHostingModal } from './TechnologyApplicationHostingModal';

const PAGE_SIZE = 10;

type ApplicationFormState = {
  name: string;
  version: string;
  repositoryUrl: string;
  pipelineUrl: string;
  azureDevOpsOrganization: string;
  azureDevOpsProject: string;
  azureDevOpsRepository: string;
  azureDevOpsPatSecretId: string;
  status: string;
  notes: string;
};

const emptyForm: ApplicationFormState = {
  name: '',
  version: '',
  repositoryUrl: '',
  pipelineUrl: '',
  azureDevOpsOrganization: '',
  azureDevOpsProject: '',
  azureDevOpsRepository: '',
  azureDevOpsPatSecretId: '',
  status: 'Draft',
  notes: '',
};

export function TechnologyApplicationsPage() {
  const [searchParams] = useSearchParams();
  const requestedApplicationId = searchParams.get('applicationId');
  const openedFromQuery = useRef<string | null>(null);
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.applications');
  const [items, setItems] = useState<TechnologyApplication[]>([]);
  const [secrets, setSecrets] = useState<TechnologySecret[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [inventoryApplication, setInventoryApplication] = useState<TechnologyApplication | null>(
    null,
  );
  const [hostingApplication, setHostingApplication] = useState<TechnologyApplication | null>(null);
  const [form, setForm] = useState<ApplicationFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadSecretOptions(): Promise<void> {
    try {
      const result = await listTechnologySecrets({ page: 1, pageSize: 200 });
      setSecrets(result.items);
    } catch {
      /* opções auxiliares — não bloquear a tela */
    }
  }

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyApplications({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
      setInventoryApplication((current) => {
        if (!current) return current;
        return result.items.find((item) => item.id === current.id) ?? current;
      });
      setHostingApplication((current) => {
        if (!current) return current;
        return result.items.find((item) => item.id === current.id) ?? current;
      });
    } catch {
      toast.error('Falha ao carregar aplicações.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadSecretOptions();
  }, []);

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  useEffect(() => {
    if (!requestedApplicationId || openedFromQuery.current === requestedApplicationId) return;
    openedFromQuery.current = requestedApplicationId;
    void getTechnologyApplication(requestedApplicationId)
      .then((item) => startEdit(item))
      .catch(() => toast.error('Aplicação não encontrada.'));
    // Abre o cadastro uma vez quando a composição pede uma aplicação específica.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedApplicationId]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: TechnologyApplication): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      version: item.version ?? '',
      repositoryUrl: item.repositoryUrl ?? '',
      pipelineUrl: item.pipelineUrl ?? '',
      azureDevOpsOrganization: item.azureDevOpsOrganization ?? '',
      azureDevOpsProject: item.azureDevOpsProject ?? '',
      azureDevOpsRepository: item.azureDevOpsRepository ?? '',
      azureDevOpsPatSecretId: item.azureDevOpsPatSecretId ?? '',
      status: item.status,
      notes: item.notes ?? '',
    });
  }

  function toPayload(): TechnologyApplicationUpsertPayload {
    const catalogItemIds = editingId
      ? (items.find((item) => item.id === editingId)?.catalogItems ?? []).map(
          (catalogItem) => catalogItem.id,
        )
      : [];

    return {
      catalogItemIds,
      name: form.name.trim(),
      version: form.version.trim() || null,
      repositoryUrl: form.repositoryUrl.trim() || null,
      pipelineUrl: form.pipelineUrl.trim() || null,
      azureDevOpsOrganization: form.azureDevOpsOrganization.trim() || null,
      azureDevOpsProject: form.azureDevOpsProject.trim() || null,
      azureDevOpsRepository: form.azureDevOpsRepository.trim() || null,
      azureDevOpsPatSecretId: form.azureDevOpsPatSecretId || null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyApplication(editingId, toPayload());
        toast.success('Aplicação atualizada.');
        collapseForm(resetForm);
      } else {
        await createTechnologyApplication(toPayload());
        toast.success('Aplicação criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar aplicação.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyApplication): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir aplicação',
      message: `Deseja excluir "${item.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyApplication(item.id);
      toast.success('Aplicação excluída.');
      if (editingId === item.id) collapseForm(resetForm);
      if (inventoryApplication?.id === item.id) setInventoryApplication(null);
      if (hostingApplication?.id === item.id) setHostingApplication(null);
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir aplicação.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Aplicações"
        description="Cadastro das aplicações. Use os ícones da listagem para inventário e hospedagem."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar aplicação' : 'Nova aplicação'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.name || 'aplicação selecionada'} - clique para expandir`
              : undefined
          }
          createSummary="Clique para expandir e cadastrar uma aplicação"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Status
                <select
                  className="erp-input"
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  {TECHNOLOGY_APPLICATION_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Versão
                <input
                  className="erp-input"
                  value={form.version}
                  onChange={(event) => setForm({ ...form, version: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Repositório
                <input
                  className="erp-input"
                  value={form.repositoryUrl}
                  onChange={(event) => setForm({ ...form, repositoryUrl: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Pipeline
                <input
                  className="erp-input"
                  value={form.pipelineUrl}
                  onChange={(event) => setForm({ ...form, pipelineUrl: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Azure — organização
                <input
                  className="erp-input"
                  value={form.azureDevOpsOrganization}
                  onChange={(event) =>
                    setForm({ ...form, azureDevOpsOrganization: event.target.value })
                  }
                  placeholder="myorg"
                />
              </label>
              <label className="erp-label">
                Azure — projeto
                <input
                  className="erp-input"
                  value={form.azureDevOpsProject}
                  onChange={(event) => setForm({ ...form, azureDevOpsProject: event.target.value })}
                  placeholder="MeuProjeto"
                />
              </label>
              <label className="erp-label">
                Azure — repositório
                <input
                  className="erp-input"
                  value={form.azureDevOpsRepository}
                  onChange={(event) =>
                    setForm({ ...form, azureDevOpsRepository: event.target.value })
                  }
                  placeholder="nome-do-repo"
                />
              </label>
              <label className="erp-label">
                Azure — PAT (cofre)
                <select
                  className="erp-input"
                  value={form.azureDevOpsPatSecretId}
                  onChange={(event) =>
                    setForm({ ...form, azureDevOpsPatSecretId: event.target.value })
                  }
                >
                  <option value="">Sem integração</option>
                  {secrets.map((secret) => (
                    <option key={secret.id} value={secret.id}>
                      {secret.name}
                      {secret.kind ? ` · ${secret.kind}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="mt-2 text-xs text-secondary-500">
              Org/projeto/repositório também podem ser inferidos da URL do repositório Azure. O PAT
              deve estar cadastrado no Cofre (Work Items + Code Read). Inventário e hospedagem ficam
              nos ícones da listagem.
            </p>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[200px]"
          value={statusFilter}
          onChange={(event) => {
            setPage(1);
            setStatusFilter(event.target.value);
          }}
        >
          <option value="">Todos os status</option>
          {TECHNOLOGY_APPLICATION_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Versão</th>
                  <th className="w-36">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-secondary-500">
                      Nenhuma aplicação cadastrada.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>
                        <CodedStatusBadge
                          status={item.status}
                          label={technologyOptionLabel(
                            TECHNOLOGY_APPLICATION_STATUS_OPTIONS,
                            item.status,
                          )}
                        />
                      </td>
                      <td>{item.version || '—'}</td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            title="Inventário"
                            aria-label="Inventário"
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-secondary-500 transition-colors hover:bg-primary-50 hover:text-primary-600"
                            onClick={() => setInventoryApplication(item)}
                          >
                            <QueueListIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            title="Hospedagem"
                            aria-label="Hospedagem"
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-secondary-500 transition-colors hover:bg-primary-50 hover:text-primary-600"
                            onClick={() => setHostingApplication(item)}
                          >
                            <ServerStackIcon className="h-5 w-5" />
                          </button>
                          {canWrite ? (
                            <>
                              <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                              <DeleteIconButton
                                onClick={() => void handleDelete(item)}
                                label="Excluir"
                              />
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}

      {inventoryApplication ? (
        <TechnologyApplicationInventoryModal
          open
          application={inventoryApplication}
          canWrite={canWrite}
          onClose={() => setInventoryApplication(null)}
          onSaved={() => loadData(page)}
        />
      ) : null}

      {hostingApplication ? (
        <TechnologyApplicationHostingModal
          open
          application={hostingApplication}
          canWrite={canWrite}
          onClose={() => setHostingApplication(null)}
        />
      ) : null}
    </PageShell>
  );
}
